import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { runProspectSync, syncReportText } from "@/lib/prospectSync";
import ProspectBoard from "@/components/shop/ProspectBoard";
import { polygonCentroid } from "@/components/map/LeafletMap";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

// Admin view of a zone's prospects — the SAME shared records the assigned shop
// sees on My Service Area. Edits here are instantly visible to the shop.
export default function ZoneProspectBoard({ zoneId, zoneName, polygon = [], boundary = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingNow, setSyncingNow] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/zones/${zoneId}/prospects`); setData(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [zoneId]);

  useEffect(() => { load(); }, [load]);

  const onUpdate = async (id, payload) => {
    const { data: p } = await api.patch(`/admin/prospects/${id}`, payload);
    setData((d) => ({ ...d, prospects: d.prospects.map((x) => (x.id === p.id ? p : x)) }));
  };

  const onSaveToRider = async (p, photos) => {
    const { data } = await api.post(`/admin/prospects/${p.id}/save-to-rider`, { photos: photos || [] });
    setData((d) => ({ ...d, prospects: d.prospects.map((x) => (x.id === data.id ? data : x)) }));
    return data;
  };

  const onRemoveFromRider = async (p) => {
    const { data } = await api.delete(`/admin/prospects/${p.id}/rider`);
    setData((d) => ({ ...d, prospects: d.prospects.map((x) => (x.id === data.id ? data : x)) }));
    return data;
  };

  const forceSync = async () => {
    setSyncingNow(true);
    try { const job = await runProspectSync({ zone_ids: [zoneId] }); await load(); toast.success(`Prospects synced: ${syncReportText(job)}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setSyncingNow(false); }
  };

  if (loading || !data) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;

  return (
    <ProspectBoard
      title={`${zoneName || "Zone"} Territory`}
      prospects={data.prospects || []}
      advertisers={data.advertisers || []}
      opportunity={data.opportunity || {}}
      osmStatus={data.osm_status}
      zonePolygons={polygon?.length >= 3 ? [polygon] : []}
      boundary={boundary || []}
      center={polygonCentroid(polygon) || undefined}
      onUpdate={onUpdate}
      onSync={forceSync}
      syncingNow={syncingNow}
      onSaveToRider={onSaveToRider}
      onRemoveFromRider={onRemoveFromRider}
    />
  );
}
